import { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { setSafMode, setSafForcedByPlatform } from '../actions/appState'

import { getSafMode, getSafForcedByPlatform } from '../selectors/appState'

import PeripheralBridge from '../utils/peripheralBridge'
import { ToastManager } from '../utils/toastManager'

import { OFFLINE_SAF_MODE, SAF_MODE_STATES } from '../VNSyncManager/containers/VNSyncManager'
import { getIsKiosk } from '../selectors/menus'

/**
 * Hook to manage SAF mode for both AFCC and PAX
 * @returns functions to manage and report SAF modes through the bridge to the APK
 */
const useSafMode = () => {
  const dispatch = useDispatch()

  // 3 - Online on Demand
  // 2 - Offline Only (Batch)
  const safMode = useSelector(getSafMode)
  const curSafMode = useRef(safMode)
  const safForcedByPlatform = useSelector(getSafForcedByPlatform)
  const isKiosk = useSelector(getIsKiosk)

  // Handle messages coming from the APK
  const registerBridgeHandler = useCallback(() => {
    const bridgeInstance = PeripheralBridge.getBridge()

    if (bridgeInstance?.callHandler) {
      bridgeInstance.registerHandler('handleSafMode', (safInteger) => {
        if (safInteger === curSafMode.current) return
        dispatch(setSafMode(safInteger))
        curSafMode.current = safInteger
      })
    }
  }, [dispatch])

  const getSafModeFromBridge = useCallback(() => {
    const bridgeInstance = PeripheralBridge.getBridge()

    if (bridgeInstance?.callHandler) {
      bridgeInstance.callHandler('getSafMode', (safString) => {
        if (safString === curSafMode.current) return
        dispatch(setSafMode(safString))
        curSafMode.current = safString
      })
    }
  }, [dispatch])

  const initiateSafBridgeCommunication = useCallback(() => {
    registerBridgeHandler()
    getSafModeFromBridge()
  }, [registerBridgeHandler, getSafModeFromBridge])

  // Send one way message to change SAF mode
  // yes - turn SAF mode to 0 or 3, logic in the APK
  // no - do not move away from SAF mode 1, APK will start coroutine to check again in 30 minutes via safOnDemandPrompt bridge message
  // offline_only - tell APK to go to offline only mode, typically only used after a manager pin approval
  const updateSafMode = useCallback(
    (safMode, forcedByPlatform = false) => {
      // Only update the SAF mode if
      //   1. the current SAF mode wasn't forced by platform and the current state wasn't forced by
      //      platform; or
      //   2. the current SAF mode was forced by platform and the current state was not forced by
      //      by platform and the new SAF mode is offline mode
      if (!forcedByPlatform && safForcedByPlatform) return

      const isOfflineMode = safMode === OFFLINE_SAF_MODE
      if (forcedByPlatform && !safForcedByPlatform && !isOfflineMode) return

      const bridgeInstance = PeripheralBridge.getBridge()
      if (!bridgeInstance?.callHandler) {
        ToastManager.error('Unable to change SAF mode!', { autoClose: true, closeOnClick: true })
        return
      }

      const updatedForcedByPlatform = isOfflineMode && forcedByPlatform

      bridgeInstance.callHandler('handleSafOfflinePrompt', {
        newSafMode: safMode,
        // isConstant means that the mode was manually changed and should not be set when in Kiosk mode
        isConstant: !isKiosk && isOfflineMode,
      })

      dispatch(setSafMode(safMode))
      dispatch(setSafForcedByPlatform(updatedForcedByPlatform))
      curSafMode.current = safMode

      ToastManager.info(`You are now in ${SAF_MODE_STATES[safMode]} mode.`, { autoClose: 3000, closeOnClick: true })
    },
    [dispatch, isKiosk, safForcedByPlatform],
  )

  return [initiateSafBridgeCommunication, updateSafMode, getSafModeFromBridge, { safMode }]
}

export default useSafMode
